import { supabase } from './supabase'

// docs/architecture/stack.md: a session ends after sixty minutes of
// inactivity, which is configuration in `settings`. This is the value used
// until that setting has been read, and when it cannot be.
export const DEFAULT_INACTIVITY_MINUTES = 60
export const INACTIVITY_SETTING_KEY = 'session.inactivity_timeout_minutes'

export const INACTIVITY_NOTICE = 'You were signed out after a period of inactivity. Sign in to continue.'

const LAST_ACTIVITY_KEY = 'muster.lastActivity'
const NOTICE_KEY = 'muster.inactivityNotice'

// The time of the last interaction is kept in localStorage, beside the
// session Supabase persists there, so activity in any tab counts and a
// browser reopened after a long absence finds how long it has been.
export function recordActivity(now: number = Date.now()): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
  } catch {
    // Storage can be unavailable; without it there is nothing to compare.
  }
}

export function clearActivity(): void {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY)
  } catch {
    // See recordActivity.
  }
}

export function lastActivity(): number | null {
  try {
    const stored = Number(localStorage.getItem(LAST_ACTIVITY_KEY))
    return Number.isFinite(stored) && stored > 0 ? stored : null
  } catch {
    return null
  }
}

// Whether the time since the last recorded interaction has reached the
// timeout. A session with nothing recorded is not inactive: the first
// interaction records one.
export function isInactive(timeoutMs: number, now: number = Date.now()): boolean {
  const last = lastActivity()
  return last !== null && now - last >= timeoutMs
}

// The configured timeout in milliseconds, read from `settings`
// (settings_read_all lets any signed-in account read it).
export async function getInactivityTimeoutMs(): Promise<number> {
  try {
    const { data } = await supabase.from('settings').select('value').eq('key', INACTIVITY_SETTING_KEY).maybeSingle()
    const minutes = Number(data?.value)
    if (Number.isFinite(minutes) && minutes > 0) return minutes * 60_000
  } catch {
    // Fall through to the default.
  }
  return DEFAULT_INACTIVITY_MINUTES * 60_000
}

export async function signOutForInactivity(): Promise<void> {
  try {
    sessionStorage.setItem(NOTICE_KEY, '1')
  } catch {
    // The sign-in page then simply carries no explanation.
  }
  clearActivity()
  await supabase.auth.signOut()
}

// Read once by the sign-in page: the explanation belongs to the sign-out
// that just happened, not to every later visit.
export function takeInactivityNotice(): string {
  try {
    if (sessionStorage.getItem(NOTICE_KEY) === null) return ''
    sessionStorage.removeItem(NOTICE_KEY)
    return INACTIVITY_NOTICE
  } catch {
    return ''
  }
}
